if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[data-variant-id]');
      this.currentVariant = this.getSelectedVariant();
      this.variantSelects = this.querySelector('variant-selects');
    }

    connectedCallback() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.addEventListener('change', this.onVariantChange.bind(this));
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));
      }
    }

    onVariantChange(event) {
      const target = event.target;
      if (!target.name || !target.name.startsWith('Option')) return;

      this.updateOptions();
      this.updateMasterId();
      this.updateVariantInput();
      this.updateURL();
      this.updateVariantStatuses();
      this.renderProductInfo();
      this.updateMedia();
    }

    updateOptions() {
      this.options = Array.from(
        this.querySelectorAll('select, input[type="radio"]:checked')
      ).map((element) => element.value);
    }

    updateMasterId() {
      const variants = this.getVariantsData();
      this.currentVariant = variants.find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateVariantInput() {
      if (!this.currentVariant) return;

      const productForm = this.querySelector('[data-product-form]');
      if (!productForm) return;

      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantStatuses() {
      const variants = this.getVariantsData();
      const options = this.querySelectorAll('.product-form__option');

      options.forEach((option) => {
        const input = option.querySelector('input');
        if (!input) return;

        const optionValue = input.value;
        const optionName = input.name;

        const isAvailable = variants.some((variant) => {
          if (!variant.available) return false;
          const optionIndex = parseInt(optionName.split('-').pop()) - 1;
          return variant.options[optionIndex] === optionValue;
        });

        const label = option.querySelector('.product-form__option-label');
        if (label) {
          if (isAvailable) {
            label.classList.remove('product-form__option-label--unavailable');
            input.disabled = false;
          } else {
            label.classList.add('product-form__option-label--unavailable');
          }
        }
      });
    }

    renderProductInfo() {
      if (!this.currentVariant) return;
      this.updatePrice();
      this.updateBuyButton();
      this.updateInventory();
      this.updateSKU();
      this.updateOptionLabels();
    }

    updatePrice() {
      const priceContainer = this.querySelector('[data-price]');
      if (!priceContainer) return;

      const variant = this.currentVariant;
      const price = priceContainer.querySelector('.price-item--regular, .price-item--sale');
      const comparePrice = priceContainer.querySelector('.price-item--compare, .price-item--regular s');
      const badge = priceContainer.querySelector('.price__badge');

      if (price) {
        price.textContent = this.formatMoney(variant.price);
      }

      if (variant.compare_at_price > variant.price) {
        priceContainer.classList.add('price--on-sale');
        if (comparePrice) {
          comparePrice.textContent = this.formatMoney(variant.compare_at_price);
          comparePrice.hidden = false;
        }
        if (badge) {
          const discount = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          badge.querySelector('.price__badge-text').textContent = `Sale -${discount}%`;
          badge.hidden = false;
        }
      } else {
        priceContainer.classList.remove('price--on-sale');
        if (comparePrice) comparePrice.hidden = true;
        if (badge) badge.hidden = true;
      }

      if (!variant.available) {
        priceContainer.classList.add('price--sold-out');
      } else {
        priceContainer.classList.remove('price--sold-out');
      }
    }

    updateBuyButton() {
      const submitButton = this.querySelector('.product-form__submit');
      const submitText = submitButton?.querySelector('.product-form__submit-text');

      if (!submitButton || !submitText) return;

      if (this.currentVariant.available) {
        submitButton.disabled = false;
        submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      } else {
        submitButton.disabled = true;
        submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }

    updateInventory() {
      const inventoryEl = this.querySelector('[id^="Inventory-"]');
      if (!inventoryEl) return;

      const variant = this.currentVariant;

      if (variant.inventory_management !== 'shopify') {
        inventoryEl.classList.add('visibility-hidden');
        return;
      }

      inventoryEl.classList.remove('visibility-hidden');
    }

    updateSKU() {
      const skuEl = this.querySelector('[id^="Sku-"]');
      if (!skuEl) return;

      if (this.currentVariant.sku) {
        skuEl.textContent = `SKU: ${this.currentVariant.sku}`;
        skuEl.classList.remove('visibility-hidden');
      } else {
        skuEl.classList.add('visibility-hidden');
      }
    }

    updateOptionLabels() {
      const labels = this.querySelectorAll('[data-option-value]');
      labels.forEach((label, index) => {
        if (this.currentVariant.options[index]) {
          label.textContent = this.currentVariant.options[index];
        }
      });
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;

      const mediaGallery = this.querySelector('[data-media-gallery]');
      if (!mediaGallery) return;

      const mediaId = this.currentVariant.featured_media.id;
      const thumbnail = mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);

      if (thumbnail) {
        thumbnail.click();
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const format = window.Shopify?.currency?.active || 'USD';

      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: format
        }).format(amount);
      } catch (e) {
        return `$${amount}`;
      }
    }

    getSelectedVariant() {
      const variantData = this.querySelector('[data-selected-variant]');
      if (!variantData) return null;

      try {
        return JSON.parse(variantData.textContent);
      } catch (e) {
        return null;
      }
    }

    getVariantsData() {
      const productData = document.querySelector(`[data-product-id="${this.dataset.productId}"]`);
      if (!productData) return [];

      if (window.productVariants && window.productVariants[this.dataset.productId]) {
        return window.productVariants[this.dataset.productId];
      }

      const variantJson = this.querySelector('[type="application/json"][data-variants]');
      if (variantJson) {
        try {
          return JSON.parse(variantJson.textContent);
        } catch (e) {
          return [];
        }
      }

      return [];
    }
  });
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', class VariantSelects extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener('change', this.onVariantChange);
    }

    onVariantChange() {
    }
  });
}
